import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import BundledEditor from './BundledEditor.jsx';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import debounce from 'lodash/debounce';

const TextEditor = ({ initialValue = '', onSave, showSaveIndicator = true, debounceTime = 1200, ...props }) => {
  const [content, setContent] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const editorRef = useRef(null);
  const isFocusedRef = useRef(false);

  // Fonction de sauvegarde avec debounce
  const saveWithDebounce = useCallback(
    debounce(async (value) => {
      // Sauvegarder la position du curseur avant de potentiellement déclencher des re-render
      const editor = editorRef.current;
      const bookmark = editor?.selection?.getBookmark?.(2, true);
      setIsSaving(true);
      try {

        // Appel à l'API pour sauvegarder le contenu
        onSave && (await onSave(value));
        // Afficher l'indicateur de succès
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 1500);

      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
      } finally {
        setIsSaving(false);
        // Restaurer la position du curseur si l'éditeur est encore présent et focalisé
        if (editor && isFocusedRef.current && bookmark) {
          setTimeout(() => {
            try {
              editor.focus();
              editor.selection.moveToBookmark(bookmark);
            } catch (_) {
              // ignore errors de restauration
            }
          }, 0);
        }
      }
    }, debounceTime),
    [onSave]
  );

  const saveContentOnBlur = useCallback(
    async (value) => {
      console.log('saveContentOnBlur', value);
      setIsSaving(true);
      try {


        // Appel à l'API pour sauvegarder le contenu
        onSave && (await onSave(value));
        // Afficher l'indicateur de succès
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 1500);

      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [onSave]
  );

  // Nettoyer le debounce lors du démontage du composant
  useEffect(() => {
    return () => {
      saveWithDebounce.cancel();
    };
  }, [saveWithDebounce]);

  // Fonction qui gère les changements dans l'éditeur
  const handleEditorChange = useCallback((newContent) => {
    setContent(newContent);
    if (debounceTime > 0) saveWithDebounce(newContent);
  }, [debounceTime, saveWithDebounce]);


  const handleBlur = useCallback(() => {
    saveContentOnBlur(content);
  }, [saveContentOnBlur, content]);

  const initConfig = useMemo(() => ({
    height: props.editorHeight || '100%',
    menubar: false,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
    ],
    toolbar: 'undo redo | formatselect | bold italic underline | ' +
      'alignleft aligncenter alignright alignjustify | ' +
      'bullist numlist outdent indent | link image | forecolor backcolor | removeformat | fullscreen',
    content_style: 'body { font-family:Poppins,Helvetica,Arial,sans-serif; font-size:14px }' +
      '.tox-tinymce { border-color: #e5e7eb !important; height: 100% !important; }' +
      '.tox-edit-focus .tox-tinymce, .tox-tinymce:focus, .tox-tinymce.focus { border-color: var(--color-primary, #4f46e5) !important; box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb, 79, 70, 229), 0.2) !important; }' +
      '.mce-content:focus { background-color: var(--color-primary, #4f46e5) !important; }',
    // Palette de couleurs alignée sur votre thème (oklch) + libellés FR
    color_map: [
      'oklch(100% 0 0)', 'Blanc',
      'oklch(98% 0 0)', 'Base 200',
      'oklch(95% 0 0)', 'Base 300',
      'oklch(21% 0.006 285.885)', 'Texte de base',
      'oklch(14% 0.005 285.823)', 'Neutre',
      'oklch(0.7687 0.1234 184.97)', 'Primaire',
      'oklch(0.5575 0.2525 302.32)', 'Secondaire',
      'oklch(0.6231 0.188 259.81)', 'Accent',
      'oklch(74% 0.16 232.661)', 'Info',
      'oklch(76% 0.177 163.223)', 'Succès',
      'oklch(82% 0.189 84.429)', 'Avertissement',
      'oklch(71% 0.194 13.428)', 'Erreur'
    ],
    color_cols: 12,
    link_default_target: '_blank',
    link_default_rel: 'noopener noreferrer',
    link_assume_external_targets: true,
    link_context_toolbar: true,
    link_quicklink: true,
    table_default_styles: { width: '100%' },
    table_class_list: [
      { title: 'Tableau simple', value: 'table' },
      { title: 'Tableau avec bordures', value: 'table table-bordered' },
      { title: 'Tableau rayé', value: 'table table-striped' },
      { title: 'Tableau compact', value: 'table table-sm' }
    ],
    table_resize_bars: true,
    table_default_attributes: { border: '1' },
    table_tab_navigation: true,
    table_cell_class_list: [
      { title: 'Cellule normale', value: '' },
      { title: 'Cellule importante', value: 'table-primary' },
      { title: 'Cellule secondaire', value: 'table-secondary' },
      { title: 'Cellule de succès', value: 'table-success' },
      { title: 'Cellule d\'avertissement', value: 'table-warning' },
      { title: 'Cellule de danger', value: 'table-danger' }
    ],
    image_advtab: true,
    image_caption: true,
    image_class_list: [
      { title: 'Responsive', value: 'img-fluid' },
      { title: 'Thumbnail', value: 'img-thumbnail' },
      { title: 'Rounded', value: 'rounded' },
      { title: 'Centré', value: 'mx-auto d-block' }
    ],
    formats: {
      alignleft: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', classes: 'text-left' },
      aligncenter: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', classes: 'text-center' },
      alignright: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', classes: 'text-right' },
      alignjustify: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', classes: 'text-justify' }
    },
    auto_resize: true,
    max_height: 800,
    min_height: 300,
    resize: true,
    statusbar: true,
    browser_spellcheck: true,
    setup: (editor) => {
      editor.addShortcut('meta+ctrl+h', 'Ouvrir le panneau d\'aide', () => editor.execCommand('mceHelp'));
      editor.addShortcut('meta+ctrl+t', 'Insérer un tableau', () => editor.execCommand('mceInsertTable'));
      editor.addShortcut('meta+ctrl+k', 'Insérer un lien', () => editor.execCommand('mceLink'));
      editor.addShortcut('meta+ctrl+shift+i', 'Insérer une image', () => editor.execCommand('mceImage'));
    }
  }), [props?.editorHeight]);

  // Synchroniser l'éditeur seulement quand la valeur externe change (p.ex. application d'un modèle)
  useEffect(() => {
    if (!editorRef.current) return;
    if (typeof initialValue !== 'string') return;
    // Ne pas écraser le contenu pendant la saisie pour éviter de déplacer le curseur
    if (isFocusedRef.current) return;

    if (content !== initialValue) {
      // Préserver la sélection si présente
      const editor = editorRef.current;
      const bookmark = editor?.selection?.getBookmark?.(2, true);
      setContent(initialValue);
      editor.setContent(initialValue || '');
      if (bookmark) {
        try {
          editor.selection.moveToBookmark(bookmark);
        } catch (_) {
          // ignore restore errors
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return (
    <div className={`relative w-full shadow-md  rounded-md ${props.containerHeight ? `h-[${props.containerHeight}]` : 'h-full'}`}>
      <BundledEditor
        onInit={(evt, editor) => editorRef.current = editor}
        initialValue={initialValue}
        onEditorChange={handleEditorChange}
        onFocus={() => { isFocusedRef.current = true; }}
        onBlur={(e) => { isFocusedRef.current = false; handleBlur(e); }}
        id={props?.id || 'tiny-editor'}
        init={initConfig}
      />

      {/* Indicateurs de sauvegarde et de succès */}
      {showSaveIndicator && (
        <>
          {isSaving && (
            <div className="z-[999] absolute bottom-9 right-2 flex items-center gap-2 bg-white px-3 py-1 rounded-full shadow-lg">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-base-content">Sauvegarde...</span>
            </div>
          )}

          {showSuccess && (
            <div className="z-[999]  absolute bottom-9 right-2 flex items-center gap-2 bg-white/ px-3 py-1 rounded-full shadow-lg">
              <CheckCircleIcon className="w-4 h-4 text-green-500" />
              <span className="text-sm text-base-content">Sauvegardé</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TextEditor;
